/**
 * Admin v2 · Weekly leaderboard rewards.
 *
 * Configures the automated Sunday-23:00-UTC job that credits the top-N
 * global weekly players with a share of a prize pool + posts the
 * winners to Telegram.
 *
 * Knobs:
 *   - enabled            (bool)   — kill switch for the whole job
 *   - total_prize_kobo   (int)    — pool split proportionally to points
 *   - top_n              (int)    — max winners (auto-shrinks to actual scorers)
 *   - bonus_pct_bps      (int)    — cash/bonus split (4000 = 40% bonus)
 *   - channel_announce   (bool)   — whether to post winners to Telegram
 *
 * Also exposes a "Run now" button that fires the job on demand. The
 * default same-week dedupe applies — pass force=true if you're
 * deliberately re-awarding after fixing a bad payout.
 *
 * Backend validates ranges on PATCH; the FE renders the specific
 * server error message inline on save failure.
 */

import { api } from '../../api.js';
import { errorState } from './common.js';

export async function renderLeaderboardRewards(host) {
  host.innerHTML = `<div style="padding:32px;color:var(--a-faint);">Loading…</div>`;
  let cfg;
  try {
    cfg = await api('/admin/settings/leaderboard-rewards');
  } catch (err) {
    host.innerHTML = errorState(err);
    return;
  }
  _mount(host, cfg);
}

function _mount(host, cfg) {
  const state = { ...cfg };

  host.innerHTML = `
    <div style="max-width:760px;">
      <div id="lbr-status" class="adm2-notice" style="display:none;"></div>

      <div class="adm2-card" style="padding:20px;margin-bottom:16px;">
        <h3 style="margin:0 0 4px;font-size:15px;">Weekly leaderboard reward</h3>
        <div style="color:var(--a-faint);font-size:12px;line-height:1.55;margin-bottom:20px;">
          Fires every Sunday at 23:00 UTC (Sunday midnight WAT, right before
          the weekly leaderboard rolls over). Top-N global players get a
          share of the pool split proportionally to points — same formula
          as live-trivia payouts. Winnings post to Telegram if the announce
          toggle is on.
        </div>

        <div style="display:flex;gap:14px;flex-wrap:wrap;">
          ${_field({
            id: 'lbr-enabled',
            label: 'Enabled',
            hint: 'Master kill switch for the job.',
            input: `
              <select id="lbr-enabled" class="adm2-input" style="width:120px;">
                <option value="true" ${state.enabled ? 'selected' : ''}>Yes</option>
                <option value="false" ${!state.enabled ? 'selected' : ''}>No</option>
              </select>
            `,
          })}
          ${_field({
            id: 'lbr-total',
            label: 'Total pool (₦)',
            hint: 'Split across winners proportionally to points.',
            input: `
              <input type="number" id="lbr-total"
                     min="0" step="100"
                     value="${(state.total_prize_kobo / 100).toFixed(0)}"
                     class="adm2-input mono" style="width:160px;text-align:right;" />
            `,
          })}
          ${_field({
            id: 'lbr-topn',
            label: 'Top-N winners',
            hint: 'Auto-shrinks if fewer players scored.',
            input: `
              <input type="number" id="lbr-topn"
                     min="1" max="20" step="1"
                     value="${state.top_n}"
                     class="adm2-input mono" style="width:100px;text-align:right;" />
            `,
          })}
          ${_field({
            id: 'lbr-bonus',
            label: 'Bonus split (%)',
            hint: '40 = 40% bonus / 60% cash per winner.',
            input: `
              <input type="number" id="lbr-bonus"
                     min="0" max="100" step="5"
                     value="${(state.bonus_pct_bps / 100).toFixed(0)}"
                     class="adm2-input mono" style="width:100px;text-align:right;" />
            `,
          })}
          ${_field({
            id: 'lbr-announce',
            label: 'Announce on Telegram',
            hint: 'Post winners to channel + group.',
            input: `
              <select id="lbr-announce" class="adm2-input" style="width:120px;">
                <option value="true" ${state.channel_announce ? 'selected' : ''}>Yes</option>
                <option value="false" ${!state.channel_announce ? 'selected' : ''}>No</option>
              </select>
            `,
          })}
        </div>
      </div>

      <div style="display:flex;gap:10px;flex-wrap:wrap;">
        <button id="lbr-save" class="adm2-btn adm2-btn-primary">Save changes</button>
        <button id="lbr-reset" class="adm2-btn">Discard</button>
        <div style="flex:1"></div>
        <button id="lbr-run" class="adm2-btn">Run now</button>
        <button id="lbr-run-force" class="adm2-btn" style="color:#E94BA2;">
          Force re-run (this week)
        </button>
      </div>

      <div style="color:var(--a-faint);font-size:11.5px;line-height:1.55;margin-top:14px;">
        <b>Run now</b> is idempotent per ISO week — same-week reruns no-op.
        <b>Force re-run</b> awards again for the same week (careful: stacks
        credits + posts a duplicate Telegram message).
      </div>

      <div id="lbr-run-result" class="adm2-card" style="display:none;margin-top:16px;padding:16px;"></div>
    </div>
  `;

  host.querySelector('#lbr-reset').addEventListener('click', () => renderLeaderboardRewards(host));

  host.querySelector('#lbr-save').addEventListener('click', async () => {
    const btn = host.querySelector('#lbr-save');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const body = {
        enabled: host.querySelector('#lbr-enabled').value === 'true',
        total_prize_kobo: Math.max(0, Math.floor(Number(host.querySelector('#lbr-total').value || 0) * 100)),
        top_n: Math.max(1, Math.floor(Number(host.querySelector('#lbr-topn').value || 3))),
        bonus_pct_bps: Math.max(0, Math.min(10_000, Math.floor(Number(host.querySelector('#lbr-bonus').value || 0) * 100))),
        channel_announce: host.querySelector('#lbr-announce').value === 'true',
      };
      const updated = await api('/admin/settings/leaderboard-rewards', {
        method: 'PATCH',
        body,
      });
      _showStatus(host, `Saved · ₦${(updated.total_prize_kobo / 100).toLocaleString()} to top ${updated.top_n} (${(updated.bonus_pct_bps / 100).toFixed(0)}% bonus)`, 'ok');
    } catch (err) {
      _showStatus(host, err.message || 'Save failed', 'err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save changes';
    }
  });

  const doRun = async (force) => {
    const btn = host.querySelector(force ? '#lbr-run-force' : '#lbr-run');
    if (force && !confirm('Force re-run for this ISO week? This will award again and post a duplicate Telegram message.')) return;
    btn.disabled = true;
    const oldLabel = btn.textContent;
    btn.textContent = 'Running…';
    try {
      const res = await api(`/admin/settings/leaderboard-rewards/run-now${force ? '?force=true' : ''}`, {
        method: 'POST',
      });
      _renderRunResult(host, res);
    } catch (err) {
      _showStatus(host, err.message || 'Run failed', 'err');
    } finally {
      btn.disabled = false;
      btn.textContent = oldLabel;
    }
  };
  host.querySelector('#lbr-run').addEventListener('click', () => doRun(false));
  host.querySelector('#lbr-run-force').addEventListener('click', () => doRun(true));
}

function _field({ label, hint, input }) {
  return `
    <label style="display:flex;flex-direction:column;gap:6px;">
      <span style="color:var(--a-faint);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;">${label}</span>
      ${input}
      <span style="color:var(--a-faint);font-size:11px;">${hint}</span>
    </label>
  `;
}

function _showStatus(host, msg, kind) {
  const el = host.querySelector('#lbr-status');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  el.style.padding = '10px 14px';
  el.style.borderRadius = '8px';
  el.style.marginBottom = '14px';
  el.style.fontSize = '13px';
  if (kind === 'ok') {
    el.style.background = 'rgba(52,211,153,0.08)';
    el.style.color = '#34D399';
    el.style.border = '1px solid rgba(52,211,153,0.35)';
  } else {
    el.style.background = 'rgba(233,75,162,0.08)';
    el.style.color = '#E94BA2';
    el.style.border = '1px solid rgba(233,75,162,0.35)';
  }
}

function _renderRunResult(host, res) {
  const el = host.querySelector('#lbr-run-result');
  if (!el) return;
  const winners = res.winners || [];
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  const rows = winners
    .map((w) => `
      <tr>
        <td style="padding:6px 12px;">${medals[w.rank] || `${w.rank}.`}</td>
        <td style="padding:6px 12px;">${w.username || '(no username)'}</td>
        <td style="padding:6px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;">${w.points.toLocaleString()} pts</td>
        <td style="padding:6px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;">₦${(w.payout_kobo / 100).toLocaleString()}</td>
        <td style="padding:6px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;color:var(--a-faint);">
          ₦${(w.cash_kobo / 100).toLocaleString()} cash + ₦${(w.bonus_kobo / 100).toLocaleString()} bonus
        </td>
      </tr>
    `)
    .join('');
  const summary = {
    awarded: `Awarded ${winners.length} winner(s) for ISO week ${res.iso_week}.`,
    already_awarded: `Already awarded for ISO week ${res.iso_week} — nothing to do (use Force re-run if you really want to award again).`,
    disabled: `Job is disabled in the config above — flip Enabled → Yes and save first.`,
    no_scorers: `No one has scored yet for ISO week ${res.iso_week}. Nothing to award.`,
  };
  el.style.display = 'block';
  el.innerHTML = `
    <div style="font-weight:600;margin-bottom:8px;">${summary[res.reason] || res.reason}</div>
    ${
      winners.length
        ? `<table style="width:100%;border-collapse:collapse;font-size:12.5px;">${rows}</table>`
        : ''
    }
    <div style="color:var(--a-faint);font-size:11.5px;margin-top:10px;">
      Announced: ${res.announced?.posted || 0} Telegram chat(s)${res.announced?.reason ? ` (${res.announced.reason})` : ''}.
    </div>
  `;
}
